/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ElementRef, Injectable } from '@angular/core';
import { NumberTypes, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import { isNotNil, setHours, setMinutes } from '@gh/core-util';
import { fromEvent$, interval$ } from '@gh/rx';
import { delay$, filter$, first$, map$, switchMap$, tap$ } from '@gh/rx/operators';
import { GridApi } from 'ag-grid-community';
import { find, first, isEmpty, isNil, map, reverse } from 'lodash';
import {
  GanttState,
  GanttStateEventType,
  queryGanttAllParentTaskIds,
  queryGanttAllTasks,
  queryGanttAncestorTaskIds,
  queryGanttDescendantTaskIds,
  queryGanttDestinationTaskByNumberForBubble,
  queryGanttDestinationTaskByNumberForMove,
  queryGanttEndDate,
  queryGanttHasInvalidTask,
  queryGanttIsDateValid,
  queryGanttIsDragAllowed,
  queryGanttLastChildIndexByNumber,
  queryGanttLeftTrimToPredecessors,
  queryGanttLeftTrimToWorkTime,
  queryGanttMultiMode,
  queryGanttNextTaskNumber,
  queryGanttRedoDisabled,
  queryGanttRightTrimToPredecessors,
  queryGanttRightTrimToWorkTime,
  queryGanttStartDate,
  queryGanttStartTask,
  queryGanttTask,
  queryGanttTaskIndex,
  queryGanttTaskPath,
  queryGanttTasksByParent,
  queryGanttTotalDuration,
  queryGanttUndoDisabled,
  queryTaskCompletionPct,
} from '../gantt-chart.state';
import {
  completionPctFromTimelineTaskProgress,
  completionPctToTimelineTaskProgress,
  GanttChartBounds,
  GanttTask,
  GanttTaskRelation,
  ganttTaskRelationFromTaskDto,
  ganttTaskRelationFromTimelineLink,
  ganttTaskRelationToTimelineLink,
  ganttTaskToTimelineTask,
  getGanttTaskId,
  HOURS_END_OF_DAY,
  HOURS_START_OF_DAY,
  isGanttTaskIncluded,
  normalizeTaskRelations,
  validateGanttTaskField,
} from '../resources/gantt.model';
import { GanttChartBusService } from './gantt-chart-bus.service';
import { parsePredecessors, ScheduleType, stringifyPredecessors } from '@gh/builder-shared-data';
import { ScheduleGanttDetails } from '../../builder-scheduling/resources/schedule-gantt-details.model';
import {
  ScheduleTemplateGanttDetails,
} from '../../builder-setup/scheduling/resources/schedule-template-gantt-details.model';
import { Observable } from 'rxjs';
import {
  GanttEditTaskDialogConfig,
  ganttEditTaskDialogRoute,
} from '../components/gantt-edit-task-dialog/gantt-edit-task-dialog.component';

const GANTT_SCROLL_NOT_ALLOWED_INTERVAL = 100;

@Injectable()
export class GanttChartCommandService {
  isUndoDisabled$ = this.state.select(queryGanttUndoDisabled);
  isRedoDisabled$ = this.state.select(queryGanttRedoDisabled);
  timer: any;
  private api: GridApi;

  constructor(
    private state: State<GanttState>,
    private bus: GanttChartBusService,
    private dialog: DialogService,
    private notifications: NotificationService,
    private elementRef: ElementRef,
  ) {
    // Here we read actual state of tasks from DHTMLX component and update tasks in state correspondingly
    // delay(0) is important here because DHTMLX component does not propagate changes immediately to descendants
    // when onAfterAutoSchedule event is called
    bus.afterAutoSchedule.pipe(delay$(0)).subscribe((tasks) => {
      // DHTMLX component notifies about auto scheduling of tasks only, but not ancestors and descendants.
      // Here we take all ancestor and descendant tasks from DHTMLX component
      // and update these tasks in state
      const ancestors = this.state.project(queryGanttAncestorTaskIds, tasks.map((task) => task.id))
                            .map((taskId) => bus.getTask(taskId));
      const descendants = this.state.project(queryGanttDescendantTaskIds, tasks.map((task) => task.id))
                              .map((taskId) => bus.getTask(taskId));
      const tasksWithRelatives = ancestors
      .concat(descendants)
      .concat(tasks)
      // exclude tasks which are not included into gantt chart (excluded tasks)
      .filter(Boolean);

      this.state.mutate({
                          type : GanttStateEventType.TaskUpdateFromTimeline,
                          tasks: tasksWithRelatives,
                        });
      this.syncTopTasksForMultiMode();
    });
    bus.afterLinkDelete.subscribe((link) => {
      // when link was added on the gantt component we have to add this link into the model
      const task = this.state.project(queryGanttTask, link.target);
      // do we have this link in model already?
      if (isNotNil(find(task.predecessors, { taskId: link.source }))) {
        this.state.mutate({
                            type  : GanttStateEventType.TaskSetField,
                            taskId: link.target,
                            field : 'predecessors',
                            value : task.predecessors ? task.predecessors.filter(
                              (predecessor) => predecessor.id !== link.id) : [],
                          });
        if (task.parentTaskId) {
          this.recalculateAncestors(task.parentTaskId);
        }
      }
      this.syncTopTasksForMultiMode();
    });
    bus.afterLinkAdd.subscribe((link) => {
      // when link was added on the gantt component we have to add this link into the model
      const task = this.state.project(queryGanttTask, link.target);
      // do we have this link in model already?
      if (isNil(find(task.predecessors, { taskId: link.source }))) {
        this.state.mutate({
                            type  : GanttStateEventType.TaskSetField,
                            taskId: link.target,
                            field : 'predecessors',
                            value : (task.predecessors || []).concat(
                              [ganttTaskRelationFromTimelineLink(link, this.state.project(queryGanttAllTasks))]),
                          });

        if (task.parentTaskId) {
          this.recalculateAncestors(task.parentTaskId);
        }
      }
      this.syncTopTasksForMultiMode();
    });

    bus.afterTaskUpdate.subscribe((timelineTask) => {
      const task = this.state.project(queryGanttTask, timelineTask.id);
      // here we track changing of progress on timeline and propagate this change to parent task
      if (timelineTask.progress !== completionPctToTimelineTaskProgress(task.completionPct || 0)) {
        this.setTaskCompletionPct(timelineTask.id, completionPctFromTimelineTaskProgress(timelineTask.progress));
      }
    });
  }

  redo(): void {
    this.state.mutate({
                        type: GanttStateEventType.Redo,
                      });
    setTimeout(() => this.api.redrawRows());
  }

  undo(): void {
    this.state.mutate({
                        type: GanttStateEventType.Undo,
                      });
    setTimeout(() => this.api.redrawRows());
  }

  initHistory(): void {
    this.state.mutate({
                        type: GanttStateEventType.InitHistory,
                      });
  }

  addToHistory(): void {
    this.state.mutate({
                        type: GanttStateEventType.AddToHistory,
                      });
  }

  getNextTaskNumber(): number {
    return this.state.project(queryGanttNextTaskNumber);
  }

  getTaskIndex(taskId: ID): number {
    return this.state.project(queryGanttTaskIndex, taskId);
  }

  getStartTask(): Maybe<GanttTask> {
    return this.state.project(queryGanttStartTask);
  }

  selectTask(taskId: ID): void {
    if (!!this.api.getSortModel().length) {
      let selectedId = '';
      for (const taskUniqId in this.state.value.tasks) {
        if (this.state.value.tasks[taskUniqId] && this.state.value.tasks[taskUniqId].selected) {
          selectedId = taskUniqId;
          break;
        }
      }
      this.state.mutate({
                          type: GanttStateEventType.SelectTask,
                          taskId,
                        });
      if (selectedId) {
        this.state.mutate({
                            type  : GanttStateEventType.UnselectTask,
                            taskId: selectedId,
                          });
      }
    } else {
      this.bus.gantt.selectTask(taskId);
    }
  }

  getSelectedTask(): Maybe<GanttTask> {
    const selectedId = this.bus.gantt.getSelectedId();
    if (selectedId) {
      return this.getTask(selectedId);
    }
    return;
  }

  getTask(taskId: ID): GanttTask {
    return this.state.project(queryGanttTask, taskId);
  }

  isIncludedTaskExist(taskId: ID): boolean {
    const task = this.state.project(queryGanttTask, taskId);
    return isNotNil(task) && isGanttTaskIncluded(task) || false;
  }

  isRelationsAllowed(taskId: ID, relations: GanttTaskRelation[]): boolean {
    return isEmpty(this.bus.validateLinks(
      taskId,
      relations.map((relation) => ganttTaskRelationToTimelineLink(taskId, relation)),
    ));
  }

  validateRelations(taskId: ID, relations: GanttTaskRelation[]): GanttTaskRelation[] {
    const result = this.bus.validateLinks(
      taskId,
      relations.map((relation) => ganttTaskRelationToTimelineLink(taskId, relation)),
    );
    return result.length ? result.filter((link) => !!link && link.id).map(
      ({ id }) => <GanttTaskRelation>find(relations, { id })) : [];
  }

  correctTaskStartDate(date: Date): Date {
    return this.bus.correctTaskStartDate(date);
  }

  showCompletedTasks(show: boolean): void {
    this.state.mutate({
                        type: GanttStateEventType.SetShowCompletedTasks,
                        show,
                      });

    this.bus.showCompletedTasks(show);
  }

  showDeletedTasks(show: boolean): void {
    this.state.mutate({
                        type: GanttStateEventType.SetShowDeletedTasks,
                        show,
                      });

    this.bus.showCompletedTasks(show);
  }

  addTasks(tasks: GanttTask[]): void {
    const hasParentTask = tasks.some((task) => isNotNil(task.parentTaskId));
    if (hasParentTask) {
      throw new Error('GanttChartCommandService: addTasks allows to add only top level tasks');
    }

    const destinationIndexInRoot = this.state.project(queryGanttLastChildIndexByNumber);
    this.state.mutate({
                        type            : GanttStateEventType.TaskAddSet,
                        tasks,
                        destinationOrder: destinationIndexInRoot,
                      });

    // Here is an intentional separation of two batch updates.
    // The first operation can lead to recalculation of task/project dates
    // and this recalculation is deferred until batch is completed.
    this.bus.batchUpdate(() => {
      this.bus.addTasks(tasks.map(ganttTaskToTimelineTask), destinationIndexInRoot);
    });
    this.bus.batchUpdate(() => {
      this.recalculateAncestors(first(tasks)!.parentTaskId!); // tslint:disable-line:no-non-null-assertion
    });
    this.syncTopTasksForMultiMode();
  }

  addTask(task: GanttTask, branchIndex?: number): void {
    const destinationIndexInParent = isNotNil(branchIndex)
      ? branchIndex
      : this.state.project(queryGanttLastChildIndexByNumber, task.parentTaskId);
    this.state.mutate({
                        type            : GanttStateEventType.TaskAdd,
                        task,
                        destination     : task.parentTaskId,
                        destinationOrder: destinationIndexInParent,
                      });

    // Here is an intentional separation of two batch updates.
    // The first operation can lead to recalculation of task/project dates
    // and this recalculation is deferred until batch is completed.
    this.bus.batchUpdate(() => {
      this.bus.addTask(ganttTaskToTimelineTask(task), destinationIndexInParent);
    });
    this.bus.batchUpdate(() => {
      if (task.parentTaskId) {
        this.recalculateAncestors(task.parentTaskId);
        this.setTaskOpened(task.parentTaskId, true);
      }
    });
    this.syncTopTasksForMultiMode();
    this.recalculateTaskNumbers();
  }

  removeTask(taskId: ID): void {
    const task = this.state.project(queryGanttTask, taskId);

    this.state.mutate({
                        type: GanttStateEventType.TaskRemove,
                        taskId,
                      });

    // Here is an intentional separation of two batch updates.
    // The first operation can lead to recalculation of task/project dates
    // and this recalculation is deferred until batch is completed.
    this.bus.batchUpdate(() => {
      this.bus.deleteTask(taskId);
    });
    this.bus.batchUpdate(() => {
      if (task.parentTaskId) {
        this.recalculateAncestors(task.parentTaskId);
      }
    });
    this.syncTopTasksForMultiMode();
    this.recalculateStartEndDates();
    this.recalculateTaskNumbers();
  }

  unmarkAsDeletedTask(taskId: ID): void {
    this.state.mutate({
                        type: GanttStateEventType.UnMarkAsDeleted,
                        taskId,
                      });
    this.bus.batchUpdate(() => {
      this.bus.unMarkAsDeletedTask(taskId);
    });
    this.syncTopTasksForMultiMode();
    this.recalculateStartEndDates();
    setTimeout(() => {
      this.reDrawRows(taskId);
    });
  }

  markAsDeletedTask(taskId: ID): void {
    const task = this.state.project(queryGanttTask, taskId);
    const parentTaskId = task.parentTaskId;
    if (parentTaskId) {
      const getRootParent = (id: string): any => {
        const item = this.state.project(queryGanttTask, id);
        if (item.parentTaskId) {
          return getRootParent(item.parentTaskId);
        }
        return id;
      };
      const rootParent = getRootParent(parentTaskId);
      const destination =  this.state.project(queryGanttDestinationTaskByNumberForBubble, this.state.value.tasksByParent[rootParent][0]);
      this.state.mutate({
                          type            : GanttStateEventType.TaskMove,
                          source          : taskId,
                          destination     : undefined,
                          destinationOrder: destination?.taskOrder || 1,
                        });
    }
    this.state.mutate({
                        type: GanttStateEventType.MarkAsDeleted,
                        taskId,
                      });

    // Here is an intentional separation of two batch updates.
    // The first operation can lead to recalculation of task/project dates
    // and this recalculation is deferred until batch is completed.
    this.bus.batchUpdate(() => {
      this.bus.markAsDeletedTask(taskId);
    });
    this.bus.batchUpdate(() => {
      if (parentTaskId) {
        this.recalculateAncestors(parentTaskId);
      }
    });
    this.syncTopTasksForMultiMode();
    this.recalculateStartEndDates();
    this.recalculateTaskNumbers();
  }

  setTaskField(taskId: ID, field: string, value: any): void {
    switch (field) {
      case 'name':
        this.setTaskName(taskId, value);
        break;
      case 'noteMemo':
        this.setTaskNote(taskId, value);
        break;
      case 'resource':
        this.setTaskResource(taskId, value);
        break;
      case 'predecessors':
        this.setTaskPredecessors(taskId, value);
        break;
      case 'duration':
        this.setTaskDuration(taskId, value);
        break;
      case 'completionPct':
        this.setTaskCompletionPct(taskId, value);
        break;
      case 'startDate':
        this.setTaskStartDate(taskId, value);
        break;
      case 'endDate':
        this.setTaskEndDate(taskId, value);
        break;
      case 'description':
        this.setTaskDescription(taskId, value);
        break;
      case 'confirmedWith':
        this.setTaskConfirmedWith(taskId, value);
        break;
      case 'confirmedOnDate':
        this.setTaskConfirmedOnDate(taskId, value);
        break;
      case 'confirmedForDate':
        this.setTaskConfirmedForDate(taskId, value);
        break;
      case 'isExcludeFromGanttChart':
        this.setTaskExcluded(taskId, value);
        break;
      default:
        break;
    }
    this.syncTopTasksForMultiMode();
  }

  isInvalid(): boolean {
    return this.state.project(queryGanttHasInvalidTask);
  }

  navigateToInvalid(): void {
    this.state.mutate({ type: GanttStateEventType.NavigateToInvalid });
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: GanttStateEventType.ResetFocusedCell });
  }

  moveTask(sourceTaskId: ID, destinationTaskId: Maybe<ID>, destinationTaskOrder: number): void {
    const sourceTask = this.state.project(queryGanttTask, sourceTaskId);
    this.state.mutate({
                        type            : GanttStateEventType.TaskMove,
                        source          : sourceTaskId,
                        destination     : destinationTaskId,
                        destinationOrder: destinationTaskOrder,
                      });

    this.bus.batchUpdate(() => {
      if (sourceTask.parentTaskId !== destinationTaskId) {
        if (sourceTask.parentTaskId) {
          this.recalculateAncestors(sourceTask.parentTaskId, true);
        }
        if (destinationTaskId) {
          this.recalculateAncestors(destinationTaskId);
        }
      }
      this.bus.moveTask(sourceTaskId, destinationTaskOrder, sourceTask.parentTaskId, destinationTaskId);
    });
    this.syncTopTasksForMultiMode();
  }

  dragTask(sourceTask: any, destinationTask?: any): void {
    const sourceTaskId = getGanttTaskId(sourceTask);
    const destinationTaskId = destinationTask ? getGanttTaskId(destinationTask) : '';
    if (!this.state.project(queryGanttIsDragAllowed, sourceTaskId, destinationTaskId)) {
      return;
    }

    const { taskId, taskOrder } = this.state.project(
      queryGanttDestinationTaskByNumberForMove,
      sourceTaskId,
      destinationTaskId,
    );

    // if (this.state.project(queryGanttIsTaskHaveCommonLinks, sourceTaskId, destinationTaskId)) {
    //   this.notifications.error('msg_sch_cyclic_dependence');
    //   return;
    // }

    this.moveTask(sourceTaskId, taskId, taskOrder);

    const relations = parsePredecessors(stringifyPredecessors(sourceTask.predecessors));

    if (isNil(relations)) {
      this.recalculateTaskNumbers();
      return;
    }

    const isRelationsAllowed = this.isRelationsAllowed(
      sourceTaskId,
      relations.map((rel) => ganttTaskRelationFromTaskDto(sourceTask, rel)),
    );
    if (!isRelationsAllowed) {
      // const { taskId, taskOrder } = this.state.project(
      //   queryGanttDestinationTaskByNumberForMove,
      //   destinationTaskId,
      //   sourceTaskId);
      if (taskId) {
        this.moveTask(taskId, sourceTaskId, taskOrder);
        this.notifications.error('msg_sch_cyclic_dependence');
      }
      return;
    }
    this.recalculateTaskNumbers();
  }

  bubbleTask(taskIdToBubble: ID): void {
    const destination = this.state.project(queryGanttDestinationTaskByNumberForBubble, taskIdToBubble);
    if (destination) {
      const { taskId, taskOrder } = destination;
      this.moveTask(taskIdToBubble, taskId, taskOrder);
    }
    this.recalculateTaskNumbers();
  }

  setTaskOpened(taskId: ID, opened: boolean): void {
    this.state.mutate({ type: GanttStateEventType.TaskOpen, taskId, opened });
    if (opened) {
      this.bus.openTask(taskId);
    } else {
      this.bus.closeTask(taskId);
    }
  }

  expandAllNotCompleted(): void {
    this.openAllNotCompleted();
  }

  expandAll(): void {
    this.openAll(true);
  }

  collapseAll(): void {
    this.openAll(false);
  }

  getAllTasks(): GanttTask[] {
    return this.state.project(queryGanttAllTasks);
  }

  getAllTasks$(): Observable<GanttTask[]> {
    return this.state.select(queryGanttAllTasks);
  }

  resetDirty(): void {
    this.state.mutate({ type: GanttStateEventType.ResetDirty });
  }

  showTaskInTimeline(taskId: ID): void {
    if (!this.api.getSortModel().length) {
      this.bus.showTask(taskId);
    }
  }

  recalculateStartEndDates(): void {
    const tasks = this.state.project(queryGanttAllTasks);
    let startDate: any = undefined;
    let endDate: any = undefined;
    tasks.forEach((task) => {
      if (!task.isExcludeFromGanttChart && !task.markedAsDeleted) {
        if (!startDate || startDate > task.startDate) {
          startDate = task.startDate;
        }
        if (!endDate || endDate < task.endDate) {
          endDate = task.endDate;
        }
      }
    });
    this.state.mutate({
                        type: GanttStateEventType.RecalculateStartEndDate,
                        startDate,
                        endDate,
                      });
  }

  calculateBounds(): GanttChartBounds {
    return {
      startDate: this.state.project(queryGanttStartDate),
      endDate  : this.state.project(queryGanttEndDate),
      duration : this.state.project(queryGanttTotalDuration),
    };
  }

  _setGridApi(api: GridApi): void {
    this.api = api;

    this.setupScrollEvents();
  }

  saveBaseDates(): void {
    this.state.mutate({ type: GanttStateEventType.SaveBaseDates });
  }

  loadTasks(schedule: ScheduleGanttDetails | ScheduleTemplateGanttDetails): void {
    this.state.mutate({
                        type : GanttStateEventType.LoadTasks,
                        tasks: schedule.tasks,
                      });
  }

  reDrawRows(taskId?: ID): void {
    const params = taskId ? {
      rowNodes: [this.api.getRowNode(taskId)],
    } : {};
    this.api.redrawRows(params);
  }

  openEditTaskDialog(taskId: string, scheduleType?: ScheduleType, isTemplate?: boolean, isCombined?: boolean): void {
    const targetTask = this.state.project(queryGanttTask, taskId);
    this.dialog.openWithInput(ganttEditTaskDialogRoute, <GanttEditTaskDialogConfig>{
      task: targetTask,
      scheduleType,
      gantt: this.bus.gantt,
      state: this.state,
      isTemplate,
      isCombined,
    }).afterClosed().pipe(
      filter$(Boolean))
        .subscribe((updatedTask: GanttTask) => {
          if (updatedTask === 'delete') {
            if (targetTask.canBeMarkedAsDeleted) {
              this.markAsDeletedTask(taskId);
            } else {
              this.removeTask(taskId);
            }
            return;
          }
          this.state.mutate(
            {
              type: GanttStateEventType.EditTaskDialog,
              task: { ...targetTask, ...updatedTask },
            });
          if (targetTask.parentTaskId) {
            this.recalculateAncestors(targetTask.parentTaskId);
          }
          this.bus.setTaskEditDialog(taskId, updatedTask);
          // tslint:disable-next-line:no-magic-numbers
          setTimeout(() => this.api.redrawRows({ rowNodes: [this.api.getRowNode(taskId)] }), 50);
        });
  }

  private setTaskName(taskId: ID, name: string): void {
    const task = this.getTask(taskId);
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'name',
                        value: name,
                      });
    this.bus.setTaskName(taskId, name, task);
  }

  private setTaskNote(taskId: ID, value: any): void {
    this.state.mutate({
                        type  : GanttStateEventType.TaskSetFieldsWithoutDirty,
                        taskId,
                        fields: {
                          noteMemo  : value.memo,
                          recentNote: value,
                        },
                      });
    this.bus.setTaskNote(taskId, value);
  }

  private setTaskResource(taskId: ID, resource: any): void {
    const task = this.getTask(taskId);
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'resource',
                        value: resource,
                      });
    this.bus.setTaskResource(taskId, resource, task);
  }

  private setTaskDescription(taskId: ID, description: string): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'description',
                        value: description,
                      });
    this.bus.setTaskDescription(taskId, description);
  }

  private setTaskConfirmedWith(taskId: ID, confirmedWith: string): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'confirmedWith',
                        value: confirmedWith,
                      });
    this.bus.setTaskDescription(taskId, confirmedWith);
  }

  private setTaskConfirmedOnDate(taskId: ID, date: Date): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'confirmedOnDate',
                        value: date,
                      });
    this.bus.setTaskConfirmedOnDate(taskId, date);
  }

  private setTaskConfirmedForDate(taskId: ID, date: Date): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'confirmedForDate',
                        value: date,
                      });
    this.bus.setTaskConfirmedOnDate(taskId, date);
  }

  private setTaskExcluded(taskId: ID, excluded: boolean): void {
    const task = this.getTask(taskId);
    const idList = [taskId];
    const linkIds: string[] = [];
    const getParents = (parentId: ID, list: string[] = []): any => {
      list.push(parentId);
      const parentTask = this.getTask(parentId);
      if (parentTask.parentTaskId) {
        return getParents(parentTask.parentTaskId, list);
      }
      return list;
    };
    const parentsList: ID[] = task.parentTaskId ? getParents(task.parentTaskId) : [];
    const taskByParent = this.state.project(queryGanttTasksByParent);

    const setChildren = (id: ID) => {
      const children = taskByParent[id];
      if (children) {
        children.forEach((child) => {
          if (!idList.includes(child)) {
            idList.push(child);
            if (taskByParent[child]) {
              return setChildren(child);
            }
          }
        });
      }
    };
    setChildren(taskId);
    if (excluded) {
      const allTasks = this.getAllTasks();
      allTasks.forEach((item) => {
        if (idList.includes(item.taskId) || parentsList.includes(item.taskId)) {
          if (item.predecessors) {
            item.predecessors.forEach((predecessor) => {
              if (!linkIds.includes(predecessor.id)) {
                linkIds.push(predecessor.id);
              }
            });
          }
          allTasks.forEach((taskJ) => {
            if (taskJ.predecessors) {
              taskJ.predecessors.forEach((predecessorJ) => {
                if (predecessorJ.taskId === item.taskId) {
                  if (!linkIds.includes(predecessorJ.id)) {
                    linkIds.push(predecessorJ.id);
                  }
                }
              });
            }
          });
        }
      });
    }
    this.state.mutate({
                        type       : GanttStateEventType.TasksSetExcl,
                        task,
                        idList,
                        parentsList: parentsList || [],
                        value      : excluded,
                      });
    if (excluded && linkIds.length) {
      this.bus.removeLinks(linkIds);
    }
    idList.forEach((id: string) => {
      this.bus.setTaskExcluded(id, excluded);
    });
    if (taskByParent[task.taskId]) {
      if (excluded) {
        this.state.mutate({
                            type  : GanttStateEventType.TaskSetField,
                            taskId: task.taskId,
                            field : 'completionPct',
                            value : 0,
                          });
      } else {
        this.recalculateAncestors(task.taskId, true);
      }
    }
    if (task.parentTaskId) {
      parentsList.forEach((id) => {
        this.recalculateAncestors(id, true);
      });
    }
    this.recalculateStartEndDates();
    setTimeout(() => {
      this.api.redrawRows();
      // tslint:disable-next-line:no-magic-numbers
    },         50);
  }

  private setTaskDuration(taskId: ID, duration: number): void {
    if (isNil(duration) || isNotNil(NumberTypes.HoursNotZero.validate(duration))) {
      return;
    }
    this.bus.setTaskDuration(taskId, duration);
  }

  private setTaskCompletionPct(taskId: ID, completionPct: number): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'completionPct',
                        value: completionPct,
                      });

    this.bus.batchUpdate(() => {
      this.bus.setTaskProgress(taskId, completionPctToTimelineTaskProgress(completionPct));

      const task = this.state.project(queryGanttTask, taskId);
      if (task.parentTaskId) {
        this.recalculateAncestors(task.parentTaskId);
      }
    });
    setTimeout(() => {
      this.api.redrawRows({ rowNodes: [this.api.getRowNode(taskId)] });
      // tslint:disable-next-line:no-magic-numbers
    },         50);
  }

  private setTaskPredecessors(taskId: ID, predecessors: GanttTaskRelation[]): void {
    const task = this.state.project(queryGanttTask, taskId);
    const normalizedPredecessors = normalizeTaskRelations(predecessors, task.predecessors || []);
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'predecessors',
                        value: normalizedPredecessors,
                      });

    // Here is an intentional separation of two batch updates.
    // The first operation can lead to recalculation of task/project dates
    // and this recalculation is deferred until batch is completed.
    this.bus.batchUpdate(() => {
      this.bus.replaceLinks(
        map(task.predecessors, (predecessor) => ganttTaskRelationToTimelineLink(taskId, predecessor)),
        map(normalizedPredecessors, (predecessor) => ganttTaskRelationToTimelineLink(taskId, predecessor)),
      );
    });
    this.bus.batchUpdate(() => {
      if (task.parentTaskId) {
        this.recalculateAncestors(task.parentTaskId);
      }
    });
  }

  private setTaskStartDate(taskId: ID, date: Date): void {
    if (!date || !this.state.project(queryGanttIsDateValid, date)) {
      return;
    }
    const task = this.state.project(queryGanttTask, taskId);
    const roundDate = setMinutes(setHours(date, HOURS_START_OF_DAY), 0);
    // trim date by task predecessors
    const trimmedToPredecessorsDate = this.state.project(queryGanttLeftTrimToPredecessors, taskId, roundDate);
    // trim date to worktime
    const trimmedToWorkTimeDate = this.state.project(queryGanttLeftTrimToWorkTime, trimmedToPredecessorsDate);
    if (isNotNil(validateGanttTaskField(task, 'startDate', trimmedToWorkTimeDate))) {
      return;
    }
    this.bus.setTaskStartDate(taskId, trimmedToWorkTimeDate);
  }

  private setTaskEndDate(taskId: ID, date: Date): void {
    if (!this.state.project(queryGanttIsDateValid, date)) {
      return;
    }
    const task = this.state.project(queryGanttTask, taskId);
    const roundDate = setMinutes(setHours(date, HOURS_END_OF_DAY), 0);
    // trim date by task predecessors
    const trimmedToPredecessorsDate = this.state.project(queryGanttRightTrimToPredecessors, taskId, roundDate);
    const trimmedToWorkTimeDate = this.state.project(queryGanttRightTrimToWorkTime, trimmedToPredecessorsDate);
    if (isNotNil(validateGanttTaskField(task, 'endDate', trimmedToWorkTimeDate))) {
      return;
    }
    this.bus.setTaskEndDate(taskId, trimmedToWorkTimeDate);
  }

  private openAllNotCompleted(): void {
    const parentTaskIds = this.state.project(queryGanttAllParentTaskIds);
    const idsToExpand: string[] = [];
    parentTaskIds.forEach((id) => {
      const task = this.getTask(id);
      // tslint:disable-next-line:no-magic-numbers
      if (!task.completionPct || task.completionPct < 100) {
        idsToExpand.push(id);
      }
    });
    this.state.mutate({ type: GanttStateEventType.TaskOpenAll, taskIds: idsToExpand, opened: true });
    this.bus.openTasks(idsToExpand);
  }

  private openAll(opened: boolean): void {
    const parentTaskIds = this.state.project(queryGanttAllParentTaskIds);

    this.state.mutate({ type: GanttStateEventType.TaskOpenAll, taskIds: parentTaskIds, opened });
    if (opened) {
      this.bus.openTasks(parentTaskIds);
    } else {
      this.bus.closeTasks(parentTaskIds);
    }
  }

  private recalculateAncestors(parentTaskId: ID, resetCompletionIfNoChildren: boolean = false): void {
    const taskPath = this.state.project(queryGanttTaskPath, parentTaskId);

    reverse(taskPath).forEach((nextTaskId, i) => {
      const timelineTask = this.bus.getTask(nextTaskId);
      const nextCompletionPct = this.state.project(
        queryTaskCompletionPct,
        nextTaskId,
        resetCompletionIfNoChildren && i === 0,
      );
      this.state.mutate({
                          type  : GanttStateEventType.TaskSetFields,
                          taskId: nextTaskId,
                          fields: {
                            completionPct: nextCompletionPct,
                            duration     : timelineTask.duration,
                            startDate    : timelineTask.start_date,
                            endDate      : timelineTask.end_date,
                          },
                        });
      this.bus.setTaskProgress(nextTaskId, completionPctToTimelineTaskProgress(nextCompletionPct));
    });
  }

  private syncTopTasksForMultiMode(): void {
    // in multi mode duration of the topmost task should be calculated in another way
    if (this.state.project(queryGanttMultiMode)) {
      this.state.mutate({ type: GanttStateEventType.TaskSyncTopsForMultiMode });
    }
  }

  private setupScrollEvents(): void {
    // tslint:disable-next-line:no-non-null-assertion
    const gridViewportEl = this.elementRef.nativeElement.querySelector('.ag-body-viewport')!;
    const gridViewportScroll$ = fromEvent$(gridViewportEl, 'scroll').pipe(
      map$(({ target: { scrollTop } }: any) => scrollTop));
    const timelineScroll$ = this.bus.scroll.pipe(
      filter$(({ deltaY }) => deltaY !== 0),
      map$(({ top }) => top),
    );

    let onlyGridEvent: boolean = false;
    let onlyTimelineEvent: boolean = false;

    gridViewportScroll$.pipe(
      // accept grid scroll event if we did not have timeline scroll event recently
      filter$(() => !onlyTimelineEvent),
      tap$((scrollTop) => {
        onlyGridEvent = true;
        this.bus.scrollVerticallyTo(scrollTop);
      }),
      // allow timeline scroll events only after specified interval
      switchMap$(() => interval$(GANTT_SCROLL_NOT_ALLOWED_INTERVAL).pipe(first$())),
    )
                       .subscribe(() => {
                         onlyGridEvent = false;
                       });

    timelineScroll$.pipe(
      // accept timeline scroll event if we did not have grid scroll event recently
      filter$(() => !onlyGridEvent),
      tap$((top) => {
        onlyTimelineEvent = true;
        gridViewportEl.scrollTop = top;
      }),
      // allow grid scroll events only after specified interval
      switchMap$(() => interval$(GANTT_SCROLL_NOT_ALLOWED_INTERVAL).pipe(first$())),
    )
                   .subscribe(() => {
                     onlyTimelineEvent = false;
                   });
  }

  private recalculateTaskNumbers(): void {
    const allTasks = this.getAllTasks();
    const value = this.state.getValue();
    allTasks.forEach((task, i) => {
      task.taskNumber = i + 1;
      value.tasks[task.taskId] = task;
    });
    allTasks.forEach((task) => {
      if (task.predecessors?.length) {
        task.predecessors.forEach((predecessor) => {
          const target = this.state.value.tasks[predecessor.taskId];
          predecessor.taskNo = target.taskNumber;
        });
      }
    });
    this.state.next(value);
    this.api.redrawRows();
  }
}
